import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { periodsApi, type PeriodDto } from "@/api/endpoints";
import { PageHeader } from "@/components/PageHeader";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { formatDate } from "@/shared/datetime";

/**
 * UC accounting.period.close — list financial periods + close / reopen.
 * Closing blocks new posts into the period (enforced server-side in PostJournalEntryHandler).
 */
export function PeriodsPage() {
  const qc = useQueryClient();
  const canClose = useAuth(s => s.hasPermission("accounting.period.close"));
  const [reopen, setReopen] = useState<PeriodDto | null>(null);
  const [reason, setReason] = useState("");

  const query = useQuery({
    queryKey: ["financial-periods"],
    queryFn: () => periodsApi.list({ pageSize: 200 }),
  });

  const closeMutation = useMutation({
    mutationFn: (p: PeriodDto) => periodsApi.close(p.id, p.rowVersion),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["financial-periods"] }),
  });
  const reopenMutation = useMutation({
    mutationFn: (args: { p: PeriodDto; reason: string }) =>
      periodsApi.reopen(args.p.id, args.reason, args.p.rowVersion),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["financial-periods"] });
      setReopen(null); setReason("");
    },
  });

  return (
    <Box>
      <PageHeader breadcrumbs={["Accounting", "Periods"]} title="Financial periods" />
      <Alert severity="info" sx={{ mb: 2 }}>
        Closing a period blocks all new journal posts into it (NFR-ACC07). Re-opening is permitted
        but always audited and should be used only for explicit late corrections.
      </Alert>
      <Paper sx={{ p: 2 }}>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Period</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.data?.items.map(p => (
              <TableRow key={p.id} hover>
                <TableCell><b>{p.name}</b></TableCell>
                <TableCell>{formatDate(p.startDate)}</TableCell>
                <TableCell>{formatDate(p.endDate)}</TableCell>
                <TableCell>
                  <Chip size="small"
                    color={p.isClosed ? "error" : "success"}
                    label={p.isClosed ? "Closed" : "Open"}
                    icon={p.isClosed ? <LockIcon /> : <LockOpenIcon />} />
                </TableCell>
                <TableCell align="right">
                  {canClose && !p.isClosed && (
                    <Button size="small" color="warning" startIcon={<LockIcon />}
                      disabled={closeMutation.isPending}
                      onClick={() => closeMutation.mutate(p)}>
                      Close
                    </Button>
                  )}
                  {canClose && p.isClosed && (
                    <Button size="small" color="info" startIcon={<LockOpenIcon />}
                      onClick={() => setReopen(p)}>
                      Reopen…
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {closeMutation.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(closeMutation.error)}</Alert>}
      </Paper>

      <Dialog open={!!reopen} onClose={() => { setReopen(null); setReason(""); }} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => { setReopen(null); setReason(""); }}>Reopen period {reopen?.name}?</ClosableDialogTitle>
        <DialogContent>
          <TextField label="Reason *" fullWidth multiline minRows={3} sx={{ mt: 1 }}
            value={reason} onChange={(e) => setReason(e.target.value)} />
          {reopenMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(reopenMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReopen(null); setReason(""); }}>Cancel</Button>
          <Button variant="contained" color="info"
            disabled={!reason || reopenMutation.isPending}
            startIcon={reopenMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <LockOpenIcon />}
            onClick={() => reopen && reopenMutation.mutate({ p: reopen, reason })}>
            Reopen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
